<header class="go-page-title">
  <h1 class="go-heading-1">{{ pageTitle }}</h1>
  <a
  [href]="linkToSource"
  target="_blank">
    <go-button buttonVariant="neutral"> View on GitHub </go-button>
  </a>
</header>

<section class="go-container">

  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <!-- Intro -->
        <p class="go-body-copy go-body-copy--no-margin">
          The <code class="code-block--inline">&lt;go-toast&gt;</code> component should be used to notify the user of event responses
          or show notices. These should be used for short messages.
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- Bindings -->
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="componentBindings"></code>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">dismissable</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          By default this is false, but if set to true it will show an <go-icon icon="close"></go-icon> icon
          on the toast and allow the toast to be removed from the UI.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">enableMaxHeight</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          By default this is false, but if set to true will allow the toast to have a max height that allows for vertical overflow scrolling.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">header</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          The text to be displayed as the header of the toast. This is optional, however either this binding or the
          <code class="code-block--inline">message</code> binding must be set.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">icon</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          Changes the icon that is displayed within the toast. This is set automatically for each type, but if a value
          is passed for this binding it will override all of those type icons. See the
          <a class="go-link" href="https://material.io/tools/icons/">Material Design System Icons</a>
          page for available icons.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">message</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          The text or non-executable HTML to be displayed as the message of the toast. This is optional, however either this binding or the
          <code class="code-block--inline">header</code> binding must be set.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">type</h4>
        <p class="go-body-copy">
          This binding determines which type of toast to display. By default, it will be set to <code class="code-block--inline">neutral</code>.
          Options for this are:
        </p>
        <ol class="go-list go-list--ordered">
          <li><code class="code-block--inline">positive</code></li>
          <li><code class="code-block--inline">neutral</code></li>
          <li><code class="code-block--inline">negative</code></li>
        </ol>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">handleDismiss</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This event gets emitted when the dismiss button is clicked. <code class="code-block--inline">dismissable</code> must be
          set to <code class="code-block--inline">true</code> for the button to appear.
        </p>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="basic-example">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Basic Example</h2>
      <go-copy cardId="basic-example" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Shows a basic use case of each type.
      </p>
      <code [highlight]="basic_html"></code>
    </ng-container>
  </go-card>

  <go-toast
    class="go-column go-column--100"
    header="Success!"
    message="The thing you did saved successfully." type="positive">
  </go-toast>
  <go-toast
    class="go-column go-column--100"
    header="Hey!"
    message="Did you know that this is pretty cool?">
  </go-toast>
  <go-toast 
    class="go-column go-column--100"
    header="Oh No!"
    message="The thing you did didn't work right." type="negative">
  </go-toast>
  <go-toast
    class="go-column go-column--100"
    header="HTML Example"
    type="positive"
    message='&#x2192; <a href="https://github.com/mobi/goponents" target="_blank">#1 Design System</a> &#x2190;'>
  </go-toast>

  <go-card class="go-column go-column--100" id="dismiss-example">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Dismiss Example</h2>
      <go-copy cardId="dismiss-example" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <h4 class="go-heading-4">component.html</h4>
      <code [highlight]="dismiss_html"></code>
      <h4 class="go-heading-4">component.ts</h4>
      <code [highlight]="dismiss_ts"></code>
    </ng-container>
  </go-card>

  <go-toast
    *ngIf="showDismissToast"
    class="go-column go-column--100"
    header="Heads Up!"
    message="We use cookies and stuff, so watch out. You can dismiss me."
    [dismissable]="true"
    (handleDismiss)="dismissed()">
  </go-toast>

  <go-card class="go-column go-column--100" id="toaster-example">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Toaster Example</h2>
      <go-copy cardId="toaster-example" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        There is by default a 3 second duration on the toast. A user can hover over the toast to pause that timer and
        then when they hover off of the toast the timer will start over.
      </p>
      <h4 class="go-heading-4">component.ts</h4>
      <code [highlight]="toaster_ts"></code>
      <h4 class="go-heading-4">app.module.ts</h4>
      <code [highlight]="toaster_app_ts"></code>
      <h4 class="go-heading-4">app.component.html</h4>
      <code [highlight]="toaster_app_html"></code>
      <go-button (handleClick)="sendToast()">Make Toast</go-button>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="button-example">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button Example</h2>
      <go-copy cardId="button-example" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
        <p class="go-body-copy">
          Toast actions are a set of buttons and other tools meant to perform actions on the Toast.
          <code class="code-block--inline">showToastActions</code> must be
          set to <code class="code-block--inline">true</code> for the action button to appear.
        </p>
      <h4 class="go-heading-4">component.html</h4>
      <code [highlight]="action_btn_html"></code>
    </ng-container>
  </go-card>

  <go-toast
    type="neutral"
    header="Hey!"
    class="go-column go-column--100"
    message="Did you know that this is pretty cool?" 
    [showToastActions]="true">
    <ng-container go-toast-action>
      <div class="go-button-group">
        <div class="go-button-group__item">
          <go-button buttonVariant="neutral">Button1</go-button>
        </div>
        <div class="go-button-group__item">
          <go-button buttonVariant="neutral">Button2</go-button>
        </div>
        <div class="go-button-group__item">
          <go-button buttonVariant="neutral">Button3</go-button>
        </div>
      </div>
    </ng-container>
  </go-toast>

  <go-card class="go-column go-column--100" id="header-template">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Header Template</h2>
      <go-copy cardId="header-template" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Instead of passing just a string to the header of the toast, an
        Angular template can be utilized. This will allow use of Angular components
        and HTML inside of the header of the toast.
        <strong>
          Note: This will replace anything passed into the <code class="code-block code-block--inline">header</code> input.
        </strong>
      </p>
      <h4 class="go-heading-4">component.html</h4>
      <code [highlight]="toast_header_template_html"></code>
    </ng-container>
  </go-card>

  <go-toast 
    class="go-column go-column--100"
    message="This toast is an example that uses a header template">
    <ng-template #headerContent>
      Header Content Template <go-icon icon="home"></go-icon>
    </ng-template>
  </go-toast>

  <go-card class="go-column go-column--100" id="message-template">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Message Template</h2>
      <go-copy cardId="message-template" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Instead of passing just a string to the message of the toast, an
        Angular template can be utilized. This will allow use of Angular components
        and HTML inside of the message of the toast.
        <strong>
          Note: This will replace anything passed into the <code class="code-block code-block--inline">message</code> input.
        </strong>
      </p>
      <h4 class="go-heading-4">component.html</h4>
      <code [highlight]="toast_message_template_html"></code>
    </ng-container>
  </go-card>

  <go-toast 
    class="go-column go-column--100"
    header="Message Content Template">
    <ng-template #messageContent>
      This toast is an example that uses a message template <go-icon icon="home"></go-icon>
    </ng-template>
  </go-toast>

  <go-card class="go-column go-column--100" id="toast-theme">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Theme</h2>
      <go-copy cardId="toast-theme" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          A dark or light theme can be triggered through the <code class="code-block--inline">@Input theme: 'dark' | 'light' = 'light';</code>
          binding. The theme signifies which color of background the element is being placed upon. A light theme will be good for a light background,
          while a dark theme would be better for something like being placed in the off canvas which has a dark background.
        </p>
      </div>
      <h4 class="go-heading-4">component.html</h4>
      <code [highlight]="toast_theme_template_html"></code>
    </ng-container>
  </go-card>

  <go-toast
    theme="dark"
    class="go-column go-column--100"
    header="Success!"
    message="The thing you did saved successfully." 
    type="positive">
  </go-toast>

  <go-card class="go-column go-column--100" id="toast-icon">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Icon Example</h2>
      <go-copy cardId="toast-icon" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          An icon may be specified with the <code class="code-block--inline">@Input icon: string;</code>
          binding. If a value is passed to this binding, it will override all of the default icons for each type.
        </p>
      </div>
      <h4 class="go-heading-4">component.html</h4>
      <code [highlight]="toast_icon_template_html"></code>
    </ng-container>
  </go-card>

  <go-toast
    class="go-column go-column--100"
    icon="pets"
    header="Icon Chaos!"
    message="This shows an example where the icon has been set to something unique from the Material Icons library.">
  </go-toast>

  <go-card class="go-column go-column--100" id="max-height">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Max Height Example</h2>
      <go-copy cardId="max-height" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>
      <div class="go-column go-column--100">
        By default, the toasts allows to expand to the size of its content. See below:
      </div>
      <div class="go-column go-column--50">
        <h4 class="go-heading-4 go-heading--underlined">View</h4>
        <go-toast header="Show me everything!">
          <ng-template #messageContent>
            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
          </ng-template>
        </go-toast>
      </div>
      <div class="go-column go-column--50">
        <h4 class="go-heading-4 go-heading--underlined">Code</h4>
        <code
          [highlight]="toast_maxHeight_html"
          class="code-block--no-bottom-margin">
        </code>
      </div>

      <div class="go-column go-column--100">
        To enable the max height on toasts, you can pass in a binding:
      </div>
      <div class="go-column go-column--50">
        <h4 class="go-heading-4 go-heading--underlined">View</h4>
        <go-toast header="Something you should know..." [enableMaxHeight]="true">
          <ng-template #messageContent>
            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
          </ng-template>
        </go-toast>
      </div>
      <div class="go-column go-column--50">
        <h4 class="go-heading-4 go-heading--underlined">Code</h4>
        <code
          [highlight]="toast_maxHeight_enable_html"
          class="code-block--no-bottom-margin">
        </code>
      </div>
    </div>
  </go-card>

</section>
